import * as image from '../../assets/index'
import ThryvLeadsModal from './thryv-leads-modal'
import {BenchmarkingToolsChoiceModal} from './benchmarking-tools-choice-modal'

const PROSPECTING_TOOLS_AUTHORIZED_ROLES = {
  packageRecommendation: [
    'admin',
    'clientcare',
    'leadconverter',
    'inside',
    'premise',
    'national',
    'reseller',
    'thryvonly',
    'tss',
    'yello',
    'premisedemo',
    'insidedemo',
    'premisepilot',
    'vivialdemo',
    'directsales',
    'partnerops',
  ],
  thryvDemoRequest: ['admin', 'premise', 'national'],
  thryvPaySavingsProposal: [
    'admin',
    'clientcare',
    'leadconverter',
    'inside',
    'premise',
    'national',
    'reseller',
    'thryvonly',
    'tss',
    'yello',
    'premisedemo',
    'insidedemo',
    'premisepilot',
    'vivialdemo',
    'australia',
    'directsales',
    'partnerops',
  ],
  thryvLeads: [
    'admin',
    'clientcare',
    'leadconverter',
    'inside',
    'premise',
    'national',
    'reseller',
    'thryvonly',
    'tss',
    'premisedemo',
    'insidedemo',
    'premisepilot',
    'vivialdemo',
    'directsales',
    'partnerops',
  ],
  thryvCSN: [
    'admin',
    'clientcare',
    'leadconverter',
    'inside',
    'premise',
    'national',
    'thryvonly',
    'tss',
    'premisedemo',
    'insidedemo',
    'premisepilot',
    'vivialdemo',
    'directsales',
    'partnerops',
  ],
  seoMBGProposal: [
    'admin',
    'clientcare',
    'leadconverter',
    'inside',
    'premise',
    'national',
    'thryvonly',
    'tss',
    'premisedemo',
    'insidedemo',
    'premisepilot',
    'vivialdemo',
    'australia',
  ],
  seoProposal: [
    'admin',
    'clientcare',
    'leadconverter',
    'inside',
    'premise',
    'national',
    'thryvonly',
    'tss',
    'premisedemo',
    'insidedemo',
    'premisepilot',
    'vivialdemo',
    'australia',
    'reseller',
    'keappartner',
  ],
}

/**
 * @typedef {{
 *   id: string;
 *   title: string;
 *   image: string;
 *   description?: string;
 *   requiredRoles?: string[];
 *   hidden?: boolean;
 * }} BaseToolItem
 *
 * @typedef {BaseToolItem & { type: 'link'; url: string }} LinkToolItem
 * @typedef {BaseToolItem & { type: 'modal'; url: string; redirectUrl?: string }} ModalToolItem
 * @typedef {BaseToolItem & { type: 'navigate'; to: string }} NavigateToolItem
 * @typedef {BaseToolItem & { type: 'custom-modal'; modal: import('react').ComponentType<{close: () => void}> }} CustomModalToolItem
 *
 * @typedef {LinkToolItem | ModalToolItem | NavigateToolItem | CustomModalToolItem} ToolItem
 */

/**
 * @typedef {{ roles: string[] }} ToolContext
 */

/**
 * Builds the sales section tool items at runtime, resolving role-dependent
 * and flag-dependent values from the provided context.
 *
 * @param {ToolContext} context
 * @returns {ToolItem[]}
 */
function buildSalesTools({roles}) {
  const isReseller = roles.includes('reseller') || roles.includes('Reseller')
  const isKeapPartner = roles.includes('keappartner')

  return [
    {
      id: 'ask-thryver',
      type: 'modal',
      title: 'Ask Thryver',
      image: image.AskthryverMascotIcon,
      url: 'ask-thryver',
    },
    {
      id: 'thryv-growth-platform-assets',
      type: 'modal',
      title: 'Thryv Growth Platform Assets',
      image: image.growthPlatformCollateralIcon,
      url: 'thryv-growth-platform-collateral',
    },
    {
      id: 'cost-estimator',
      type: 'modal',
      title: 'Cost Estimator',
      image: image.CostEstimator,
      url: 'cost-estimator',
      requiredRoles: PROSPECTING_TOOLS_AUTHORIZED_ROLES.packageRecommendation,
    },
    {
      id: 'thryv-leads',
      type: 'custom-modal',
      title: 'Thryv Leads',
      image: image.thryvLeadsLogo,
      modal: ThryvLeadsModal,
      requiredRoles: PROSPECTING_TOOLS_AUTHORIZED_ROLES.thryvLeads,
    },
    {
      id: 'seo-proposal',
      type: isReseller || isKeapPartner ? 'link' : 'modal',
      title: 'SEO Proposal',
      image: image.OOESeoProposal,
      url:
        isReseller || isKeapPartner
          ? 'https://seodashboard.thryv.com/auth0/thryv'
          : 'seotools',
      requiredRoles: PROSPECTING_TOOLS_AUTHORIZED_ROLES.seoProposal,
    },
    {
      id: 'thryv-demo-request',
      type: 'navigate',
      title: 'Thryv Demo Request',
      image: image.thryvDemoRequestIcon,
      to: '/new-demo-request',
      requiredRoles: PROSPECTING_TOOLS_AUTHORIZED_ROLES.thryvDemoRequest,
    },
    {
      id: 'thryvpay-savings-proposal',
      type: 'link',
      title: 'ThryvPay Savings Proposal',
      image: image.thryvPaySavingsProposal,
      url: process.env.REACT_APP_THRYV_PAY_SAVINGS_PROPOSAL_URL,
      requiredRoles: PROSPECTING_TOOLS_AUTHORIZED_ROLES.thryvPaySavingsProposal,
    },
    {
      id: 'thryv-csn',
      type: 'link',
      title: 'Thryv CSN',
      image: image.thryvCSN,
      url: 'https://leads.thryv.com/',
      requiredRoles: PROSPECTING_TOOLS_AUTHORIZED_ROLES.thryvCSN,
    },
    {
      id: 'thryv-promotional-offers',
      type: 'modal',
      title: 'Thryv Promotional Offers',
      image: image.ooePromotionalOffersIcon,
      url: 'promotional-offers',
    },
    {
      id: 'sales-presentations',
      type: 'modal',
      title: 'Sales Presentations',
      image: image.salesPresentationIcon,
      url: 'sales-presentations',
    },
    {
      id: 'sales-battle-cards',
      type: 'modal',
      title: 'Sales Battle Cards',
      image: image.salesBattleCardIcon,
      url: 'sales-battle-cards',
    },
    ...SALES_AND_SUPPORT_MATERIALS,
  ]
}

/**
 * @type {Array<ToolItem>}
 */
const EDUCATION_AND_DEMOS = [
  {
    id: 'global-learning-resources',
    type: 'link',
    title: 'Global Learning Resources',
    url: 'https://collateral.thryv.info/learning-hub-navigation',
    image: image.globalLearningHubLogo,
  },
  {
    id: 'marketing-center-demos',
    type: 'modal',
    title: 'Thryv Marketing Center Demos',
    url: 'marketing-center-demos',
    image: image.marketingCenterDemoLogo,
    requiredRoles: [
      'admin',
      'leadconverter',
      'premisepilot',
      'reseller',
      'insidedemo',
      'premise',
      'national',
      'tss',
      'australia',
      'newzealandsales',
      'inside',
      'premisedemo',
      'clientcare',
      'partnerops',
    ],
  },
  {
    id: 'business-center-demos',
    type: 'modal',
    title: 'Thryv Business Center Demos',
    url: 'business-center-demos',
    image: image.businessCenterDemoLogo,
  },
  {
    id: 'command-center-demos',
    type: 'modal',
    title: 'Thryv Command Center Demos',
    url: 'command-center-demos',
    image: image.commandCenterDemosIcon,
  },
  {
    id: 'thryv-support-center',
    type: 'link',
    title: 'Thryv Support Center',
    url: 'https://learn.thryv.com/hc/en-us',
    image: image.supportCenter,
  },
  {
    id: 'reporting-center-demos',
    type: 'modal',
    title: 'Thryv Reporting Center Demos',
    url: 'reporting-center-demos',
    image: image.thryvReportingCenterDemosIcon,
  },
  {
    id: 'thryvpay-demos',
    type: 'modal',
    title: 'ThryvPay Demos',
    url: 'thryvpay-demos',
    image: image.thryvPayDemoLogo,
  },
  {
    id: 'marketplace-demos',
    type: 'modal',
    title: 'Thryv Marketplace Demos',
    url: 'marketplace-demos',
    image: image.marketplaceDemosIcon,
  },
  {
    id: 'thryv-specialties',
    type: 'link',
    title: 'Thryv Specialties',
    url: 'http://industries.thryv.info/',
    image: image.specialitiesLogo,
    hidden: true,
    requiredRoles: [
      'admin',
      'clientcare',
      'leadconverter',
      'inside',
      'premise',
      'national',
      'reseller',
      'thryvonly',
      'tss',
      'yello',
      'premisedemo',
      'insidedemo',
      'premisepilot',
      'vivialdemo',
    ],
  },
  {
    id: 'workforce-center-demos',
    type: 'modal',
    title: 'Thryv Workforce Center Demos',
    url: 'workforce-center-demos',
    image: image.workforceCenterLogo,
  },
]

/**
 * @type {Array<ToolItem>}
 */
const SALES_AND_SUPPORT_MATERIALS = [
  {
    id: 'product-at-a-glance',
    type: 'link',
    title: 'Product at a Glance',
    url: 'https://thryv.sharepoint.com/:x:/s/ProductMarketing/ETKE6A05KK9BuoAnnvCcMjUBkROONLfROtyH0vow9xw-Pg?e=dtYEdX&wdLOR=c1C52DCD3-8831-9E48-9364-6CFB829A0B0EConnect%20your%20OneDrive%20account',
    image: image.ProductAtGlance,
  },
  {
    id: 'product-leave-behinds',
    type: 'modal',
    title: 'Product Leave Behinds',
    url: 'product-leave-behinds',
    image: image.leaveBehindsIcon,
  },
  {
    id: 'other-leave-behinds',
    type: 'modal',
    title: 'Other Leave Behinds',
    url: 'other-leave-behinds',
    image: image.otherLeaveBehinds,
  },
  {
    id: 'local-ads',
    type: 'modal',
    title: 'Local Ads (US)',
    url: 'local-ads',
    image: image.localAdsIcon,
  },
  {
    id: 'webinar',
    type: 'link',
    title: 'Webinar',
    url: 'https://collateral.thryv.info/webinars',
    image: image.webinarIcon,
  },
  // {
  //   id: 'benchmarking-tool',
  //   type: 'modal',
  //   title: 'Benchmarking Tool',
  //   url: 'https://tsabenchmarks.sandbox.thryv.com/',
  //   image: image.benchmarkingToolIcon,
  // },
  {
    id: 'benchmarking-tools',
    type: 'custom-modal',
    title: 'Benchmarking Tools',
    image: image.benchmarkingToolIcon,
    modal: BenchmarkingToolsChoiceModal,
  },
]

/**
 * @typedef {{
 *   id: string;
 *   label: string;
 *   path: string;
 *   build: (context: ToolContext) => ToolItem[];
 * }} ToolSection
 */

/**
 * @type {Array<ToolSection>}
 */
export const TOOL_SECTIONS = [
  {
    id: 'sales',
    label: 'Sales & Support Materials',
    path: '/tools/sales',
    build: context => buildSalesTools(context),
  },
  {
    id: 'education',
    label: 'Education & Demos',
    path: '/tools/education',
    build: () => EDUCATION_AND_DEMOS,
  },
]
